/**
 * Focus management helpers (§10): trap focus inside modal layers, restore focus to the opener
 * on close, and close on Escape. Shared by Dialog and Sheet.
 */
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true' && !el.closest('[inert]'),
  );
}

/** Keep Tab/Shift+Tab inside `ref` while `active`. */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const root = ref.current;
      if (event.key !== 'Tab' || !root) return;
      const items = focusableWithin(root);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        event.preventDefault();
        root.focus();
        return;
      }
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !root.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !root.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [ref, active]);
}

/** Where focus goes when the element that opened a layer has gone: the open sheet's title, else main. */
function focusFallback(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>('section[role="dialog"] h2[tabindex="-1"]') ??
    document.getElementById('main')
  );
}

/**
 * Give focus back after a layer closed, if focus is still inside the layer or lost to <body>.
 * If the opener is gone (re-rendered, unmounted) or unfocusable, focus moves to the sheet
 * underneath or the main region instead of <body>. Deferred so the layer's DOM is gone first.
 */
export function restoreFocus(opener: Element | null, layer: HTMLElement | null): void {
  const active = document.activeElement;
  const lost = !active || active === document.body || (layer?.contains(active) ?? false);
  if (!lost) return;
  queueMicrotask(() => {
    // Still mounted (e.g. React re-running effects in development): nothing closed.
    if (layer?.isConnected) return;
    const now = document.activeElement;
    if (now && now !== document.body && now.isConnected && !(layer?.contains(now) ?? false)) return;
    const destination =
      opener instanceof HTMLElement &&
      opener !== document.body &&
      opener.isConnected &&
      !opener.closest('[inert]')
        ? opener
        : focusFallback();
    destination?.focus({ preventScroll: true });
  });
}

/**
 * The element that had focus when a layer opened. Kept in a ref so that effects re-running while
 * the layer stays open (focus is then inside the layer) don't replace the real opener.
 */
export function useOpener(ref: RefObject<HTMLElement | null>) {
  const opener = useRef<Element | null>(null);
  return () => {
    const active = document.activeElement;
    if (!opener.current || !(ref.current?.contains(active) ?? false)) opener.current = active;
    return opener;
  };
}

/** Remember what had focus when the layer opened and give it back when the layer closes. */
export function useRestoreFocus(ref: RefObject<HTMLElement | null>): void {
  const capture = useOpener(ref);
  useLayoutEffect(() => {
    const opener = capture();
    const layer = ref.current;
    return () => restoreFocus(opener.current, layer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
}

/**
 * Call `onEscape` when Escape is pressed. Only the topmost layer handles it: modal dialogs
 * (`priority` 1) always beat sheets and hints (0); within a priority the newest layer wins.
 */
export function useEscape(onEscape: (() => void) | undefined, active = true, priority = 0): void {
  const handler = useRef(onEscape);
  useLayoutEffect(() => {
    handler.current = onEscape;
  });
  useEffect(() => {
    if (!active) return;
    const entry: EscapeEntry = {
      priority,
      run: (event) => {
        if (event.defaultPrevented || !handler.current) return;
        event.preventDefault();
        handler.current();
      },
    };
    escapeStack.push(entry);
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && topEscape() === entry) entry.run(event);
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
      const i = escapeStack.lastIndexOf(entry);
      if (i >= 0) escapeStack.splice(i, 1);
    };
  }, [active, priority]);
}

interface EscapeEntry {
  priority: number;
  run(event: KeyboardEvent): void;
}
const escapeStack: EscapeEntry[] = [];

function topEscape(): EscapeEntry | undefined {
  let top: EscapeEntry | undefined;
  for (const entry of escapeStack) if (!top || entry.priority >= top.priority) top = entry;
  return top;
}

let openModals = 0;

/** Count a modal layer as open until the returned function is called (Dialog uses this). */
export function trackModal(): () => void {
  openModals += 1;
  let closed = false;
  return () => {
    if (closed) return;
    closed = true;
    openModals -= 1;
  };
}

/** Whether any modal dialog (or full-height modal sheet) is open right now. */
export function anyModalOpen(): boolean {
  return openModals > 0 || document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}

/** Subscribe to a CSS media query. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  );
}

/**
 * Focus a screen heading unless a modal layer opened in the same commit already took focus
 * (parents' layout effects run after their children's).
 */
export function focusHeading(el: HTMLElement | null): void {
  if (!el) return;
  const active = document.activeElement;
  if (active instanceof Element && active.closest('[role="dialog"][aria-modal="true"], [role="alertdialog"]'))
    return;
  el.focus({ preventScroll: true });
}
